import React, {useEffect, useMemo, useRef} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, FlexRow, Label, Pill, PillVariant, Text, TextArea, TextField} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {useInlineCategoryCreation} from '../state/useInlineCategoryCreation';
import {NovaCategoryOption} from '../modals/CreateAgentModal';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {NovaModel} from '../shared/novaModelList';
import {validateInput} from '../shared/novaInputValidation';
import {MonospacePromptEditor} from './MonospacePromptEditor';
import {NovaVariableLibrary} from './NovaVariableLibrary';
import {NovaModelPicker} from './NovaModelPicker';
import {NovaInputIssues} from './NovaInputIssues';
import {NovaReferenceFile, NovaReferenceFiles} from './NovaReferenceFiles';
import {NovaTestSection} from './NovaTestSection';

// One rhythm for every section on the tab (design system spacing step 800). The sections are
// self-contained blocks — several carry their own border or dashed outline — so at the step below this
// they crowd each other and stop reading as separate things. Kept identical on both tabs so switching
// between them doesn't shift the layout.
const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 32px;
`;

const CategoryContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// Same unstyled wrapper CreateAgentModal's category picker uses: Pill renders no click handler
// and accepts no data-cy, so this is the minimal way to make a Pill clickable and individually
// selectable without adding visual styling of its own.
const ChipButton = styled.button`
	all: unset;
	display: inline-flex;
	cursor: pointer;
`;

// Tall enough to work with multi-paragraph prompts without immediately needing to scroll.
const EDITOR_HEIGHT = 220;

interface Props {
	// Identity of the agent being authored, needed only by the dry-run section at the bottom.
	agentId: string;
	agentName: string;
	draft: NovaAgentDraft;
	categoryOptions: NovaCategoryOption[];
	variables: NovaVariable[];
	// The removed variables, needed only to tell a reference to one from a name that never existed.
	removed: NovaVariable[];
	// The models an author may choose between for this agent.
	models: NovaModel[];
	onCreateCategory: (name: string) => Promise<NovaCategoryOption>;
	// The agent's attached reference documents, plus the attach/remove actions for them. Attaching or
	// removing re-runs the page's detail fetch, which refreshes both this list and the Files-scope
	// variables merged into `variables`.
	referenceFiles: NovaReferenceFile[];
	onAttachFile: (file: File) => Promise<void>;
	onRemoveFile: (fileId: string) => Promise<void>;
}

// The System and User prompt template editors plus the category picker, all bound to the shared
// working draft. None of these save on their own — choosing a category only calls
// `draft.setCategoryId`, which marks the draft dirty; the header Save (⌘S / Save button) is the
// single point where the draft, including the category, is committed as a new version. The
// variable library sits directly above the User-prompt editor and inserts at the draft's current
// cursor position for whichever target is active. A category can also be created inline here (same
// flow as the New-agent modal) so authors don't have to leave the tab to organise an agent.
export function NovaPromptTab({
	agentId,
	agentName,
	draft,
	categoryOptions,
	variables,
	removed,
	models,
	onCreateCategory,
	referenceFiles,
	onAttachFile,
	onRemoveFile,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, activeTarget, lastPromptTarget, insertTokenAtCursor} = draft.promptState;

	// Captured once, at mount, rather than read as a plain dependency: this only needs to ask "did
	// this tab's input already own the insertion target when it appeared", never "does it own it
	// right now" — the latter would re-run the check on every focus change within this tab (e.g.
	// switching between the two editors here) and risk fighting a target the author just set.
	const arrivingTargetRef = useRef(activeTarget);

	// Also captured once, at mount, for the same reason: the claim below fires only on arrival, so it
	// must use whichever editor was last used as of that arrival, not whatever it drifts to afterward
	// while this tab stays mounted (e.g. focusing the other editor here would otherwise retarget an
	// effect that already ran).
	const lastPromptTargetRef = useRef(lastPromptTarget);

	// Arriving here from the agent.md tab restores whichever of the System prompt or User prompt
	// template the author had last focused in this tab, so a variable clicked in the library before
	// either editor here is refocused lands back where they were working rather than always in the
	// User template. Arriving with the System or User target already active — e.g. stepping away to
	// the Deploy tab and back — leaves it alone instead, since there is nothing foreign to reclaim.
	// Only the claim itself resets the caret to end-of-input (the editor remounts unfocused either
	// way, so a stale caret from a prior visit is never visible); leaving the target alone leaves the
	// caret alone too.
	useEffect(() => {
		if (arrivingTargetRef.current === PromptTarget.AgentMd) {
			setActiveTarget(lastPromptTargetRef.current);
		}
	}, [setActiveTarget]);

	const categoryLabel = formatMessage({id: 'nova.prompt.categoryLabel'});
	const insertTargetHint = formatMessage({id: 'nova.prompt.insertTargetHint'});
	const userPromptIssues = useMemo(
		() => validateInput(values.userPromptTemplate, PromptTarget.User, {offered: variables, removed}),
		[values.userPromptTemplate, variables, removed]
	);
	const systemPromptIssues = useMemo(
		() => validateInput(values.systemPrompt, PromptTarget.System, {offered: variables, removed}),
		[values.systemPrompt, variables, removed]
	);

	const category = useInlineCategoryCreation({
		categoryOptions,
		onCreateCategory,
		onCategoryCreated: created => draft.setCategoryId(created.categoryId),
	});

	return (
		<Container data-cy="nova-prompt-tab" data-testid="nova-prompt-tab">
			<TextField
				label={formatMessage({id: 'nova.prompt.titleLabel'})}
				data-cy="nova-prompt-title"
				placeholder={formatMessage({id: 'nova.prompt.titlePlaceholder'})}
				value={draft.title}
				onValueChange={(value: string) => draft.setTitle(value)}
				size={TextField.SIZE.LARGE}
				width={TextField.WIDTH.FLEX}
			/>
			<TextArea
				label={formatMessage({id: 'nova.prompt.descriptionLabel'})}
				data-cy="nova-prompt-description"
				placeholder={formatMessage({id: 'nova.prompt.descriptionPlaceholder'})}
				value={draft.description}
				onChange={(value: string) => draft.setDescription(value)}
				onBlur={() => undefined}
				isOptional={false}
				height={80}
				width={TextArea.WIDTH.FLEX}
			/>
			<CategoryContainer data-cy="nova-prompt-category" role="group" aria-label={categoryLabel}>
				<Label>{categoryLabel}</Label>
				<FlexRow wrap gap={'s'}>
					{category.allCategories.map(option => {
						const isSelected = draft.categoryId === option.categoryId;
						return (
							<ChipButton
								key={option.categoryId}
								type="button"
								data-cy={`nova-prompt-category-${option.categoryId}`}
								aria-pressed={isSelected}
								onClick={() => draft.setCategoryId(option.categoryId)}
							>
								<Pill variant={isSelected ? PillVariant.HIGHLIGHT : PillVariant.NEUTRAL}>{option.name}</Pill>
							</ChipButton>
						);
					})}
					{!category.isAdding && (
						<Button emphasis="low" icon="plus" data-cy="nova-prompt-add-category" onPress={category.startAdding}>
							{formatMessage({id: 'nova.prompt.newCategoryPrompt'})}
						</Button>
					)}
				</FlexRow>
				{category.isAdding && (
					<div onKeyDown={category.handleKeyDown}>
						<FlexRow gap={'s'} alignItems={'center'}>
							<TextField
								data-cy="nova-prompt-new-category-name"
								value={category.name}
								onValueChange={category.setName}
								placeholder={formatMessage({id: 'nova.prompt.newCategoryPlaceholder'})}
								size={TextField.SIZE.LARGE}
								width={TextField.WIDTH.FLEX}
							/>
							<Button
								emphasis="low"
								data-cy="nova-prompt-confirm-new-category"
								disabled={category.isCreating || !category.name.trim()}
								onPress={() => void category.confirm()}
							>
								{formatMessage({id: 'nova.prompt.addCategory'})}
							</Button>
						</FlexRow>
						{category.error && (
							<Text color="error" data-cy="nova-prompt-create-category-error">
								{formatMessage({id: 'nova.prompt.createCategoryError'})}
							</Text>
						)}
					</div>
				)}
			</CategoryContainer>
			<NovaModelPicker
				models={models}
				value={draft.model}
				onChange={model => draft.setModel(model)}
				dataCy="nova-prompt-model"
			/>
			<NovaReferenceFiles files={referenceFiles} onAttach={onAttachFile} onRemove={onRemoveFile} />
			<NovaVariableLibrary
				variables={variables}
				activeTarget={activeTarget}
				onInsert={token => insertTokenAtCursor(token)}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.userLabel'})}
				placeholder={formatMessage({id: 'nova.prompt.userPlaceholder'})}
				dataCy="nova-user-prompt"
				target={PromptTarget.User}
				value={values.userPromptTemplate}
				height={EDITOR_HEIGHT}
				onChange={(value: string) => setValue(PromptTarget.User, value)}
				onActivate={setActiveTarget}
				caption={activeTarget === PromptTarget.User ? insertTargetHint : undefined}
				issues={
					userPromptIssues.length > 0 ? (
						<NovaInputIssues issues={userPromptIssues} dataCy="nova-user-prompt-issues" />
					) : undefined
				}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.systemLabel'})}
				placeholder={formatMessage({id: 'nova.prompt.systemPlaceholder'})}
				dataCy="nova-system-prompt"
				target={PromptTarget.System}
				value={values.systemPrompt}
				height={EDITOR_HEIGHT}
				onChange={(value: string) => setValue(PromptTarget.System, value)}
				onActivate={setActiveTarget}
				caption={activeTarget === PromptTarget.System ? insertTargetHint : undefined}
				issues={
					systemPromptIssues.length > 0 ? (
						<NovaInputIssues issues={systemPromptIssues} dataCy="nova-system-prompt-issues" />
					) : undefined
				}
			/>
			{/*
			 * Keyed on the agent, so switching agents rebuilds the section rather than re-rendering it
			 * in place. The shell does not remount this tab on a switch — it only forces the tab strip
			 * back to Prompt — and the section holds a conversation: the turns, the selected task, and
			 * the thread id the next follow-up is sent to. Carried across a switch, a follow-up would go
			 * out under the new agent's id carrying the previous agent's thread, and the server replays
			 * a thread by its id alone, without regard to which agent is asking.
			 */}
			<NovaTestSection
				key={agentId}
				agentId={agentId}
				agentName={agentName}
				draft={draft}
				variables={variables}
				removedVariables={removed}
				models={models}
			/>
		</Container>
	);
}
